/**
 * Travel Impact Calculator, test-bypass URL passthrough.
 *
 * The backend's public quota / rate-limit middlewares
 * (`backend/middleware/travelImpactCalculatorQuota.js` and
 * `backend/middleware/publicGeocodingQuota.js`) both honour a
 * `?test=1` query parameter that bypasses the limits, so the team
 * can exercise the page in QA without burning the 20/day quota or
 * waiting out the 2-second floor between requests.
 *
 * The bypass is only useful if the *API call* carries `?test=1`, and
 * the frontend never forwarded the page URL's query string to its
 * fetch calls. This helper reads the page URL once and returns the
 * suffix the API clients should append to their request URLs.
 *
 * Why a module instead of inlining the URLSearchParams check in each
 * caller: there are two callers today (`api.ts`, `geocodingApi.ts`)
 * and the rule needs to stay identical across both — drift between
 * them would create the surprising condition where the calculator
 * is unlocked but typeahead is still rate-limited, or vice versa.
 *
 * Phase 7, post-launch UX pass.
 */

const TEST_QUERY_KEY = 'test';
const TEST_QUERY_VALUE = '1';

/**
 * True iff the page URL currently carries `?test=1` (or `&test=1`).
 * Defensive against SSR/JSDOM edge cases where `window` is missing.
 */
export function isTestBypassActive(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(TEST_QUERY_KEY) === TEST_QUERY_VALUE;
}

/**
 * Returns `'?test=1'` when the bypass is active, otherwise the empty
 * string. Use as a suffix on backend URLs:
 *
 *   `${API_BASE}/public/co2/calculate-batch${testBypassQuerySuffix()}`
 *
 * The suffix always uses `?` (not `&`) because the API endpoints
 * receiving it don't otherwise take query parameters today. If that
 * ever changes, replace the literal with proper URLSearchParams
 * composition.
 */
export function testBypassQuerySuffix(): string {
  return isTestBypassActive() ? `?${TEST_QUERY_KEY}=${TEST_QUERY_VALUE}` : '';
}

/**
 * Appends `&test=1` (or `?test=1`) to an existing URL when the bypass
 * is active, otherwise returns the URL untouched. Used to keep the
 * test flag in the browser's address bar through `history.replaceState`
 * after a successful calculation, so a QA session that starts with
 * `?test=1` stays in test mode for subsequent calcs instead of
 * silently dropping back into the rate-limited flow.
 *
 * Implementation note: explicit query-string concatenation rather
 * than `URL`/`URLSearchParams` so this works for both absolute and
 * relative URLs and doesn't require a base argument.
 */
export function withTestBypass(url: string): string {
  if (!isTestBypassActive()) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${TEST_QUERY_KEY}=${TEST_QUERY_VALUE}`;
}
